import React, { useEffect, useState } from "react";
import { PageHeader } from "../../components/Shell";
import { api, API_URL, tokenStore, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Download } from "lucide-react";

export default function MemberDocuments() {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/documents");
      setDocs(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/me/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      e.target.value = "";
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/me/documents/${id}`);
    load();
  };

  const download = async (id, filename) => {
    const res = await fetch(`${API_URL}/me/documents/${id}/download`, {
      headers: { Authorization: `Bearer ${tokenStore.get()}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="member-docs-page">
      <PageHeader
        eyebrow="Member"
        title="Documents"
        actions={
          <label className="px-4 py-2 rounded-md bg-[#05126E] text-white font-semibold hover:bg-[#030B45] transition-colors flex items-center gap-2 cursor-pointer">
            <Upload className="w-4 h-4" />
            <span>{busy ? "Uploading…" : "Upload"}</span>
            <input type="file" className="hidden" onChange={upload} disabled={busy} data-testid="upload-input" />
          </label>
        }
      />

      <div className="border border-[#E4E4E7] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F7F8] border-b border-[#E4E4E7]">
            <tr className="text-left">
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Filename</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Size</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Uploaded</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-zinc-500">No documents uploaded yet.</td></tr>
            )}
            {docs.map((d) => (
              <tr key={d.id} className="border-b border-[#E4E4E7] hover:bg-[#F7F7F8]">
                <td className="px-4 py-3 font-semibold">{d.filename}</td>
                <td className="px-4 py-3 mono">{(d.size / 1024).toFixed(1)} KB</td>
                <td className="px-4 py-3 mono">{new Date(d.uploaded_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => download(d.id, d.filename)}
                      className="p-1.5 rounded-md border border-[#D2D8D4] hover:border-[#0f1a14] hover:bg-[#F4F6F5] transition-colors">
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(d.id)}
                      className="p-1.5 border border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
